import { getDayZeroQuestInfo } from "@/components/Discover/utils";
import { useQuestCompletions } from "@/components/QuestsV2/useQuestCompletions";
import { appConfig } from "@/constants";
import {
  getDiscoverFeed,
  getQuestSections,
} from "@/domain/gems-game/data-access/lib/cms";
import { getPromoTiles, getQuests } from "@imx-tokens-ts/gems-game/data-access";
import { useRouter } from "next/router";
import useSWR, { preload, type SWRConfiguration } from "swr";

type Props = {
  isFetchingLatestEarningsPaused: boolean;
}; // How long ago the user completed the onboarding quest
export const QUESTS_DATA_KEY = "/quests";
export const QUESTS_SECTIONS_DATA_KEY = "/quest_sections";
export const QUESTS_PROMO_DATA_KEY = "/quest_promo_tiles";
export const DISCOVER_FEED_DATA_KEY = "/discover_feed";

export const usePromoTiles = ({ isFetchingLatestEarningsPaused }: Props) =>
  useSWR(isFetchingLatestEarningsPaused ? null : QUESTS_PROMO_DATA_KEY, () =>
    getPromoTiles(fetch, appConfig.PROMO_TILES_CMS_URL),
  );

export const useQuests = () =>
  useSWR(QUESTS_DATA_KEY, () => getQuests(fetch, appConfig.QUESTS_CMS_URL));

export const useQuestSections = () =>
  useSWR(QUESTS_SECTIONS_DATA_KEY, () =>
    getQuestSections(fetch, appConfig.QUEST_SECTIONS_CMS_URL),
  );

type DiscoverFeedOptions = {
  isOnboarding?: boolean;
  swrConfig?: SWRConfiguration;
};

export const useDiscoverFeed = ({
  isOnboarding = false,
  swrConfig,
}: DiscoverFeedOptions = {}) => {
  const { completedQuests, isLoading } = useQuestCompletions(undefined, true);

  const router = useRouter();
  const { onboardingExperience } = router.query as {
    onboardingExperience?: string;
  };

  const completedQuestIds = Object.keys(completedQuests ?? {});
  const { isDay0 } = getDayZeroQuestInfo(completedQuests);

  const feedResponse = useSWR(
    [
      DISCOVER_FEED_DATA_KEY,
      onboardingExperience,
      completedQuestIds,
      isOnboarding,
    ],
    () =>
      getDiscoverFeed({
        fetch,
        collectionItemsUrl: appConfig.DISCOVER_FEED_CMS_URL,
        isOnboarding,
        isDay0,
        onboardingExperience,
        completedQuestIds,
      }),
    {
      ...swrConfig,
      keepPreviousData: true,
    },
  );

  return {
    ...feedResponse,
    isLoading: feedResponse.isLoading || isLoading,
    isDay0,
  };
};

export const preloadAllQuestCMS = async () => {
  preload(QUESTS_DATA_KEY, () => getQuests(fetch, appConfig.QUESTS_CMS_URL));
  preload(QUESTS_SECTIONS_DATA_KEY, () =>
    getQuestSections(fetch, appConfig.QUEST_SECTIONS_CMS_URL),
  );
  preload(QUESTS_PROMO_DATA_KEY, () =>
    getPromoTiles(fetch, appConfig.PROMO_TILES_CMS_URL),
  );
  preload(DISCOVER_FEED_DATA_KEY, () =>
    getDiscoverFeed({
      fetch,
      collectionItemsUrl: appConfig.DISCOVER_FEED_CMS_URL,
      isOnboarding: false, // Users only see the onboarding feed if they arrive from paid advertising outside of Immutable Play
    }),
  );
};
